/**
 * Compass Explorer — domain-specific filter chips.
 *
 * Each chip carries a curated predicate over a `PersistedNode`. When
 * one or more chips are active, `buildExplorerScene` evaluates the
 * union (OR) of their predicates against every leaf and stamps
 * `attrs.filterDimmed=true` on the misses. Renderers already honour
 * `attrs.dimmed`; the new `filterDimmed` is identical but scoped so
 * focus/lens-driven dimming and chip-driven dimming don't clobber
 * each other in transcripts.
 *
 * Why a Compass-local module instead of the shared rules engine:
 * - Predicates can be domain-specific (e.g. "finding" reads
 *   `props.kind === 'finding'` which the generic condition evaluator
 *   has no vocabulary for without a custom operator).
 * - Chips are stateless toggles, not the full
 *   condition/and-or/effect mini-language. UX should feel like a
 *   filter pill, not a query builder.
 */
import type { PersistedNode } from '../../models/KnowledgeGraph';

export interface CompassFilterChip {
  /** Stable id used by the prefs store. */
  id: string;
  /** Short label shown on the chip. */
  label: string;
  /** lucide-react icon name (resolved in the component). */
  iconId:
    | 'dollar'
    | 'alert'
    | 'globe'
    | 'clock'
    | 'user'
    | 'shuffle'
    | 'git-branch'
    | 'flame'
    | 'shield';
  /** Tooltip / longer description. */
  description: string;
  /**
   * Tailwind class fragment for the active state accent ring. Keeps
   * the chip palette in lockstep with the underlying meaning (cost =
   * amber, risk = red, exposure = orange, etc.).
   */
  activeClass: string;
  /**
   * Pure predicate over a graph leaf. Receives the attributed monthly
   * spend map so cost-related chips can read the same per-resource
   * estimate the heatmap uses, instead of re-deriving from props.
   */
  matches: (node: PersistedNode, ctx: ChipEvalContext) => boolean;
}

export interface ChipEvalContext {
  monthlySpendById: ReadonlyMap<string, number>;
}

function props(node: PersistedNode): Record<string, unknown> {
  return (node.props ?? {}) as Record<string, unknown>;
}

function numericProp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function tag(node: PersistedNode, names: readonly string[]): string | undefined {
  const p = props(node);
  const tags = p.tags;
  if (!tags || typeof tags !== 'object' || Array.isArray(tags)) return undefined;
  const rec = tags as Record<string, unknown>;
  for (const name of names) {
    const v = rec[name];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  const lower = new Map(Object.entries(rec).map(([k, v]) => [k.toLowerCase(), v]));
  for (const name of names) {
    const v = lower.get(name.toLowerCase());
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

export const COMPASS_FILTER_CHIPS: readonly CompassFilterChip[] = [
  {
    id: 'has-cost',
    label: 'Has cost',
    iconId: 'dollar',
    description: 'Resources carrying attributed monthly spend.',
    activeClass: 'border-amber-500 bg-amber-50 text-amber-900',
    matches: (node, ctx) => (ctx.monthlySpendById.get(node.id) ?? 0) > 0,
  },
  {
    id: 'cost-waste',
    label: 'Waste',
    iconId: 'flame',
    description: 'Top 20% spenders, or idle with measurable spend.',
    activeClass: 'border-rose-500 bg-rose-50 text-rose-900',
    matches: (node, ctx) => {
      const spend = ctx.monthlySpendById.get(node.id) ?? 0;
      if (spend <= 0) return false;
      const util = numericProp(props(node).utilizationPct ?? props(node).utilization);
      if (util != null && util <= 20) return true;
      return spend >= 50;
    },
  },
  {
    id: 'findings',
    label: 'Findings',
    iconId: 'alert',
    description: 'Anomalies, policy violations, drift, or coverage gaps.',
    activeClass: 'border-red-500 bg-red-50 text-red-900',
    matches: (node) => {
      const p = props(node);
      return (
        p.kind === 'finding' ||
        p.kind === 'cost_anomaly' ||
        p.kind === 'policy_violation' ||
        p.kind === 'coverage_gap' ||
        p.kind === 'attack_path'
      );
    },
  },
  {
    id: 'public-exposure',
    label: 'Public',
    iconId: 'globe',
    description: 'Resources reachable from the internet.',
    activeClass: 'border-orange-500 bg-orange-50 text-orange-900',
    matches: (node) => {
      const p = props(node);
      if (p.publicExposure === true) return true;
      if (typeof p.publicExposure === 'string') {
        const v = p.publicExposure.toLowerCase();
        if (v === 'open' || v === 'public' || v === 'internet') return true;
      }
      const access = p.access as Record<string, unknown> | undefined;
      if (access && typeof access === 'object') {
        const ex = access.publicExposure;
        if (ex === true) return true;
        if (typeof ex === 'string') {
          const v = ex.toLowerCase();
          if (v === 'open' || v === 'public' || v === 'internet') return true;
        }
      }
      return false;
    },
  },
  {
    id: 'stale',
    label: 'Stale 90d+',
    iconId: 'clock',
    description: 'Resources with no activity / changes in 90+ days.',
    activeClass: 'border-slate-500 bg-slate-100 text-slate-900',
    matches: (node) => {
      const age = numericProp(props(node).ageDays);
      return age != null && age >= 90;
    },
  },
  {
    id: 'unowned',
    label: 'Unowned',
    iconId: 'user',
    description: 'Missing owner / team / cost-center tag.',
    activeClass: 'border-violet-500 bg-violet-50 text-violet-900',
    matches: (node) => {
      const p = props(node);
      const owner =
        p.owner ?? p.ownerEmail ?? p.team ?? tag(node, ['Owner', 'owner', 'Team', 'team']);
      const cc = p.costCenter ?? tag(node, ['CostCenter', 'costCenter', 'cost-center']);
      return !owner || !cc;
    },
  },
  {
    id: 'drift',
    label: 'Drift',
    iconId: 'git-branch',
    description: 'Resources that diverged from the accepted baseline.',
    activeClass: 'border-yellow-500 bg-yellow-50 text-yellow-900',
    matches: (node) => {
      const p = props(node);
      const drift = p.driftStatus ?? p.baselineDelta ?? p.changeStatus;
      return typeof drift === 'string' && drift.trim() !== '' && drift !== 'unchanged';
    },
  },
  {
    id: 'cross-account',
    label: 'Cross-account',
    iconId: 'shuffle',
    description: 'Resources with trust into other AWS accounts.',
    activeClass: 'border-cyan-500 bg-cyan-50 text-cyan-900',
    matches: (node) => {
      const p = props(node);
      if (p.crossAccount === true) return true;
      const txt = JSON.stringify(p).toLowerCase();
      return txt.includes('cross-account') || txt.includes('crossaccount');
    },
  },
  {
    id: 'production',
    label: 'Production',
    iconId: 'shield',
    description: 'env / Environment tag = prod.',
    activeClass: 'border-emerald-500 bg-emerald-50 text-emerald-900',
    matches: (node) => {
      const p = props(node);
      const env =
        (typeof p.env === 'string' ? p.env : undefined) ??
        tag(node, ['Environment', 'environment', 'env', 'Env']);
      return typeof env === 'string' && /^prod(uction)?$/i.test(env.trim());
    },
  },
];

/**
 * Evaluate active chips against a leaf and return whether it should
 * remain visible. Empty `activeIds` always returns `true` (no filter
 * active = nothing dimmed).
 */
export function nodePassesActiveChips(
  node: PersistedNode,
  activeIds: ReadonlySet<string>,
  ctx: ChipEvalContext,
): boolean {
  if (activeIds.size === 0) return true;
  for (const chip of COMPASS_FILTER_CHIPS) {
    if (!activeIds.has(chip.id)) continue;
    if (chip.matches(node, ctx)) return true;
  }
  return false;
}
